<template>
  <div class="card">
    <div class="table-responsive">
      <table
        class="table table-sm align-middle mb-0"
        data-testid="forward-mandates"
      >
        <thead>
          <tr>
            <th class="ps-3">
              {{ $t("soldera_inventory.forward.columns.technology") }}
            </th>
            <th>{{ $t("soldera_inventory.forward.columns.country") }}</th>
            <th>{{ $t("soldera_inventory.forward.columns.scheme") }}</th>
            <th>{{ $t("soldera_inventory.forward.columns.cod_age") }}</th>
            <th>{{ $t("soldera_inventory.forward.columns.support") }}</th>
            <th>{{ $t("soldera_inventory.forward.columns.ecolabels") }}</th>
            <th class="text-end">
              {{ $t("soldera_inventory.forward.columns.capacity") }}
            </th>
            <th class="pe-3 take-column">
              {{ $t("soldera_inventory.forward.columns.take") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="mandates.length === 0">
            <td
              :colspan="8"
              class="text-center text-body-tertiary py-4"
              data-testid="no-mandates"
            >
              {{ $t("soldera_inventory.forward.no_mandates") }}
            </td>
          </tr>
          <tr
            v-for="mandate in mandates"
            :key="mandate.id"
            :data-testid="`mandate-${mandate.id}`"
          >
            <td class="ps-3">
              {{ mandate.technologies.map(technologyName).join(", ") }}
            </td>
            <td>{{ mandate.countries.join(", ") }}</td>
            <td>{{ mandate.registration_authority }}</td>
            <td>{{ mandate.oldest_cod_age ?? "–" }}</td>
            <td>
              {{ $t(`forwards.common.support.${mandate.support}`) }}
            </td>
            <td>
              <span
                v-for="key in mandate.ecolabels"
                :key="key"
                class="badge rounded-pill me-1"
                :class="ecolabelBadgeClass(key)"
              >
                {{ ecolabelBrandName(key) }}
              </span>
              <span v-if="mandate.ecolabels.length === 0">–</span>
            </td>
            <td class="text-end tabular-nums">{{ mandate.capacity_total }}</td>
            <td class="pe-3">
              <input
                type="number"
                class="form-control form-control-sm"
                min="0"
                step="1"
                :max="mandate.capacity_total"
                :value="takes[mandate.id] ?? ''"
                :aria-label="$t('soldera_inventory.forward.columns.take')"
                :data-testid="`take-${mandate.id}`"
                @input="onTakeInput(mandate, $event)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TakeMap } from "@/forwards2/picker/distributeTakes";
import { technologyName } from "@/types/technology";
import { ecolabelBadgeClass, ecolabelBrandName } from "@/utils/ecolabelBadges";

import type { ForwardInventoryMandate } from "../solderaInventoryTypes";

/**
 * The forward mandates a buyer may take from, one row per mandate described by what it produces, with a
 * whole-MWh take input capped at what the mandate can still sell over the picked quarters. One capacity
 * per mandate and no column per period: each mandate covers the range at its own width.
 */
defineProps<{
  mandates: ForwardInventoryMandate[];
}>();

const takes = defineModel<TakeMap>("takes", { required: true });

function onTakeInput(mandate: ForwardInventoryMandate, event: Event) {
  const raw = Number((event.target as HTMLInputElement).value);
  const volume = Number.isFinite(raw)
    ? Math.min(Math.max(Math.floor(raw), 0), mandate.capacity_total)
    : 0;
  const next = { ...takes.value };
  if (volume > 0) next[mandate.id] = volume;
  else delete next[mandate.id];
  takes.value = next;
}
</script>

<style lang="scss" scoped>
.take-column {
  width: 120px;
}

.tabular-nums {
  font-variant-numeric: tabular-nums;
}
</style>
